import { type FC, useState } from 'react'
import { Card } from 'react-bootstrap'
import classNames from 'classnames'
import { type RangeKey } from '../userScoreRange'
import { AREA_LIST_CAP, selectAreasToImprove, type AreaToImprove } from '../userScoreResultAreas'
import { UserAreaToImproveRow } from '../UserAreaToImproveRow/UserAreaToImproveRow'
import styles from './UserAreasToImproveCard.module.scss'

interface AreasToImproveCardProps {
  areas: AreaToImprove[]
  totalMeetings: number
  range: RangeKey
  userId: string
  hasScore: boolean
  canViewMeetings: boolean
}

const LINK_CLASS = 'btn btn-link p-0 insights-link border-0 bg-transparent paragraph-small text-start'

export const UserAreasToImproveCard: FC<AreasToImproveCardProps> = ({ areas, totalMeetings, range, userId, hasScore, canViewMeetings }) => {
  // Reveals the areas hidden behind the scattered-flags empty state
  const [revealed, setRevealed] = useState(false)
  // Reveals everything past the first AREA_LIST_CAP rows
  const [expanded, setExpanded] = useState(false)

  const { state, areas: listAreas, flaggedAreaCount } = selectAreasToImprove(areas, totalMeetings)
  const showRows = state === 'ranked' || revealed
  const visibleAreas = expanded ? listAreas : listAreas.slice(0, AREA_LIST_CAP)

  const subtitle =
    state === 'ranked' ? (
      'Ranked by how often each was flagged'
    ) : state === 'scattered' ? (
      <>
        No single area stands out. {flaggedAreaCount} areas were flagged occasionally across {totalMeetings} {totalMeetings === 1 ? 'meeting' : 'meetings'}, but
        none often enough to focus on.
      </>
    ) : (
      'Nothing flagged often enough to focus on.'
    )

  return (
    <Card className='zocks-border-color-gray-200 zocks-border-0.75'>
      <Card.Body className='d-flex flex-column p-4 zocks-gap-1'>
        <div className={classNames('d-flex flex-column', styles.cardHeader)}>
          <h5 className='insights-section-title mb-0'>Areas to improve</h5>
          <p className='insights-section-subtitle mb-0'>{hasScore ? subtitle : 'Not enough data'}</p>
        </div>
        {hasScore ? (
          <>
            {state === 'scattered' ? (
              <div>
                <button type='button' className={LINK_CLASS} onClick={() => setRevealed((previous) => !previous)}>
                  {revealed ? 'Show less' : 'Show them anyway'}
                </button>
              </div>
            ) : null}
            {showRows ? (
              <div>
                {visibleAreas.map((area, index) => (
                  <div key={area.title} className={classNames('py-1', index === 0 ? 'pt-0' : null)}>
                    <UserAreaToImproveRow area={area} range={range} userId={userId} canViewMeetings={canViewMeetings} />
                  </div>
                ))}
              </div>
            ) : null}
            {showRows && listAreas.length > AREA_LIST_CAP ? (
              <div className='d-flex zocks-py-0.75'>
                <button type='button' className={LINK_CLASS} onClick={() => setExpanded((previous) => !previous)}>
                  {expanded ? 'Show less' : `Show all ${listAreas.length}`}
                </button>
              </div>
            ) : null}
          </>
        ) : null}
      </Card.Body>
    </Card>
  )
}
